import React from "react";
import {
  CheckIcon,
  ClockIcon,
  EnvelopeIcon,
  PhoneIcon,
  UserGroupIcon,
} from "@heroicons/react/24/outline";
import {
  LifecycleCycle,
  LifecycleGroup,
  LifecycleUnit,
} from "./DataServices/EventHistoryDataService";
import {
  bookedSlot,
  eventsForGroup,
  formatDay,
  formatSlot,
  groupLabel,
} from "./TimelineModel";

interface Props {
  unit: LifecycleUnit;
  cycle: LifecycleCycle;
  selectedGroupKey: string;
  onSelectGroup: (groupKey: string) => void;
}

/** The tenancy has no id of its own — a cycle only ever has one. */
export const groupKey = (group: LifecycleGroup) => group.id ?? "tenant";

const SlotBadge: React.FC<{ start: string; end?: string; cancelled: boolean }> = ({
  start,
  end,
  cancelled,
}) => (
  <span
    className={`mt-1 inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[10px] font-medium ${
      cancelled
        ? "border-gray-200 bg-gray-50 text-gray-500 line-through"
        : "border-blue-200 bg-blue-50 text-blue-700"
    }`}
  >
    <ClockIcon className="h-3 w-3 flex-shrink-0" />
    {formatSlot(start, end)}
  </span>
);

const GroupCard: React.FC<{
  group: LifecycleGroup;
  meta?: string;
  slot: ReturnType<typeof bookedSlot>;
  selected: boolean;
  onClick: () => void;
}> = ({ group, meta, slot, selected, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={selected}
    className={`block w-full rounded-lg border text-left shadow-sm transition-colors ${
      selected
        ? "border-blue-500 bg-blue-50/50 ring-1 ring-blue-500/30"
        : "border-gray-200 bg-white hover:border-blue-300 hover:bg-gray-50"
    }`}
  >
    <span className="flex items-start gap-2 px-3 py-2">
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-1.5">
          <span
            className={`truncate text-xs font-semibold ${
              selected ? "text-blue-700" : "text-gray-900"
            }`}
            title={groupLabel(group)}
          >
            {groupLabel(group)}
          </span>
          <span className="inline-flex flex-shrink-0 items-center gap-1 rounded-full border border-gray-200 px-1.5 py-px text-[10px] font-medium text-gray-500">
            <UserGroupIcon className="h-3 w-3" />
            {group.members.length}
          </span>
        </span>
        {meta && (
          <span className="block truncate text-[11px] text-gray-500">{meta}</span>
        )}
        {slot && (
          <SlotBadge start={slot.start} end={slot.end} cancelled={slot.cancelled} />
        )}
      </span>
      {selected && (
        <CheckIcon className="h-4 w-4 flex-shrink-0 text-blue-600" />
      )}
    </span>

    {/* Members are always shown — no expand/collapse. */}
    <span className="block space-y-1 px-2 pb-2">
      {group.members.map((member, index) => (
        <span
          key={member.id ?? `${member.name}-${index}`}
          className="block rounded border border-gray-200 bg-white px-2 py-1"
        >
          <span className="flex items-center justify-between gap-2">
            <span className="truncate text-[11px] font-medium text-gray-900">
              {member.name}
            </span>
            {member.isGuarantor && (
              <span className="inline-flex flex-shrink-0 items-center rounded bg-purple-50 px-1.5 py-px text-[10px] font-medium text-purple-700 ring-1 ring-inset ring-purple-700/10">
                Guarantor
              </span>
            )}
          </span>
          <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[10px] text-gray-500">
            {member.email && (
              <span className="inline-flex min-w-0 items-center gap-1">
                <EnvelopeIcon className="h-2.5 w-2.5 flex-shrink-0" />
                <span className="truncate">{member.email}</span>
              </span>
            )}
            {member.phone && (
              <span className="inline-flex flex-shrink-0 items-center gap-1">
                <PhoneIcon className="h-2.5 w-2.5" />
                {member.phone}
              </span>
            )}
          </span>
        </span>
      ))}
    </span>
  </button>
);

const TimelineGroupPanel: React.FC<Props> = ({
  unit,
  cycle,
  selectedGroupKey,
  onSelectGroup,
}) => {
  const tenantGroup = cycle.groups.find((g) => g.kind === "tenant");
  const enquirers = cycle.groups.filter((g) => g.kind === "prospect");

  return (
    <aside className="flex h-64 min-h-0 flex-col gap-4 overflow-y-auto rounded-xl border border-gray-200 bg-white p-3 shadow-sm lg:h-auto">
      {tenantGroup && (
        <section>
          <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
            Tenants
          </p>
          <GroupCard
            group={tenantGroup}
            meta={unit.leaseTo ? `Lease ends ${formatDay(unit.leaseTo)}` : undefined}
            slot={null}
            selected={selectedGroupKey === groupKey(tenantGroup)}
            onClick={() => onSelectGroup(groupKey(tenantGroup))}
          />
        </section>
      )}

      <section>
        <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
          Enquirers ({enquirers.length})
        </p>
        {enquirers.length === 0 ? (
          <p className="text-[11px] text-gray-500">
            No enquiries recorded in this cycle.
          </p>
        ) : (
          <div className="space-y-2">
            {enquirers.map((group) => (
              <GroupCard
                key={group.id}
                group={group}
                slot={bookedSlot(eventsForGroup(cycle, group))}
                selected={selectedGroupKey === groupKey(group)}
                onClick={() => onSelectGroup(groupKey(group))}
              />
            ))}
          </div>
        )}
      </section>
    </aside>
  );
};

export default TimelineGroupPanel;
